/**
 * Custom hook for event page analytics tracking
 * Consolidates duplicated analytics logic from detail.tsx and donate.tsx
 */

import { useEffect } from "react";
import { useRouter } from "next/router";
import {
  useMixpanel,
  useGA,
  useAppcues,
  useSegment,
} from "@dreambigger/shared/src/hooks";
import InspireAnalytics, {
  InspireAnalyticEvents,
  InspireAnalyticsHelper,
} from "@dreambigger/shared/src/utils/inspire-analytics-helper";
import { Event as EventType, Community, OrgBootstrap, User } from "@dreambigger/shared/src/types";

export interface UseEventAnalyticsProps {
  event: EventType;
  community: Community;
  orgBootstrap: OrgBootstrap;
  user?: User;
  userLoading: boolean;
}

/**
 * Hook that handles analytics tracking for event pages
 * Tracks page views and identifies users with Appcues
 */
export const useEventAnalytics = ({
  event,
  community,
  orgBootstrap,
  user,
  userLoading,
}: UseEventAnalyticsProps) => {
  const router = useRouter();
  const mixpanel = useMixpanel();
  const segment = useSegment();
  const googleAnalytics = useGA();
  const appcues = useAppcues();

  useEffect(() => {
    // Wait for user loading to complete before tracking
    if (userLoading) {
      return;
    }

    // Skip if essential data is missing
    if (!community?.slug || !event) {
      return;
    }

    // Set up Google Analytics
    googleAnalytics.setDefaultDimensions(community.slug, user);
    googleAnalytics.pageview(window.location.pathname);

    // Track page view with Mixpanel/Segment
    InspireAnalytics.track({
      eventName: InspireAnalyticEvents.PAGE_VIEW,
      properties: {
        Page: window.location.pathname,
        "Page Type": InspireAnalyticsHelper.getPageType(router.route),
        "Host Name": window.location.host,
      },
      mixpanel,
      segment,
    });

    // Identify user with Appcues if logged in
    if (user?.id) {
      appcues.identify(user.id.toString(), {
        firstName: user.firstName,
        lastName: user.lastName,
        created_at: new Date(),
        communitySlug: community.slug,
        orgName: orgBootstrap?.name,
        projectTitle: event.name,
        projectVisibility: event.visibilityStatus,
        projectType: !!event.isFundraiser,
        eventId: event.id,
        groupId: event.id,
        imageId: user.imageId,
      });
    }
  }, [
    userLoading,
    user,
    community?.slug,
    event?.id,
    event?.name,
    event?.visibilityStatus,
    event?.isFundraiser,
    orgBootstrap?.name,
    router.route,
    googleAnalytics,
    mixpanel,
    segment,
    appcues,
  ]);

  // Return analytics instances for manual tracking if needed
  return {
    mixpanel,
    segment,
    googleAnalytics,
    appcues,
  };
};

export default useEventAnalytics;
